// modules/freshcards/src/BlockRegistryAdapter.ts
//
// 2026-09-03 (Phase 13) — Block Registry Adapter.
//
// Allows external apps (Homeschool App, VibeCoder, FreshCloud) to
// register custom block types that surface in FreshCards as
// first-class PageBlocks. The external component becomes a peer
// of DatabaseViewBlock, PageReferenceBlock, SemanticClusterBlock,
// etc. — no forks, no special render path.
//
// The adapter is a PURE module: it owns a registry of type→config
// mappings. The host app (e.g. PageBlocksSurface) consults the
// registry when rendering. Renderer mounting is a string-name or
// string-component-key the host resolves to a React component at
// render time. This keeps the adapter free of React while still
// supporting custom UI.

import type { PageBlock } from '../embedding/types';

// ---------------------------------------------------------------------------
// External block contract
// ---------------------------------------------------------------------------

/** A renderer is identified by a string. The host app resolves
 *  the string to a React component (lazy import). */
export type RendererName = string;

/** A JSON-Schema-like description of the data shape. Used by
 *  external apps to validate their records and by FreshCards to
 *  describe the block in templates. */
export interface DataContract {
  /** JSON-Schema-ish property list. Each entry is a primitive. */
  fields: DataField[];
  /** Free-form description. */
  description?: string;
}

export type DataField =
  | { kind: 'string'; name: string; required?: boolean; default?: string }
  | { kind: 'number'; name: string; required?: boolean; default?: number }
  | { kind: 'boolean'; name: string; required?: boolean; default?: boolean }
  | { kind: 'enum'; name: string; options: string[]; required?: boolean; default?: string }
  | { kind: 'object'; name: string; fields: DataField[]; required?: boolean }
  | { kind: 'array'; name: string; item: DataField; required?: boolean };

export interface ExternalBlockConfig {
  /** Block type name (e.g. 'flashcard', 'quiz', 'calendar-event'). */
  type: string;
  /** Human label shown in the block-insert menu. */
  label: string;
  /** Icon emoji or url. */
  icon: string;
  /** Renderer key — host app resolves to a React component. */
  renderer: RendererName;
  /** Data contract. */
  dataContract: DataContract;
  /** Optional default data when the block is inserted. */
  defaultData?: Record<string, any>;
  /** Optional category for the picker (e.g. 'embed', 'media', 'data'). */
  category?: 'embed' | 'media' | 'data' | 'interactive' | 'other';
}

// ---------------------------------------------------------------------------
// PageBlock shape for external blocks
// ---------------------------------------------------------------------------

/** A PageBlock that wraps an external block. The host's renderer
 *  for the type reads `data` (validated by `dataContract`) and the
 *  the adapter's `config` (for settings). */
export interface ExternalPageBlock extends PageBlock {
  type: 'external';
  externalType: string;
  data: Record<string, any>;
  config: {
    renderer: RendererName;
    category?: string;
  };
}

// ---------------------------------------------------------------------------
// Registry (singleton)
// ---------------------------------------------------------------------------

const REGISTRY = new Map<string, ExternalBlockConfig>();

export function registerExternalBlock(config: ExternalBlockConfig): void {
  REGISTRY.set(config.type, config);
}

export function unregisterExternalBlock(type: string): void {
  REGISTRY.delete(type);
}

export function getExternalBlockConfig(type: string): ExternalBlockConfig | undefined {
  return REGISTRY.get(type);
}

export function listExternalBlockConfigs(): ExternalBlockConfig[] {
  return Array.from(REGISTRY.values());
}

export function clearExternalBlockRegistry(): void {
  REGISTRY.clear();
}

// ---------------------------------------------------------------------------
// Data validation (light, schema-ish)
// ---------------------------------------------------------------------------

export interface ValidationResult {
  ok: boolean;
  errors: string[];
}

export function validateExternalBlockData(
  config: ExternalBlockConfig,
  data: Record<string, any>,
): ValidationResult {
  const errors: string[] = [];
  function check(field: DataField, value: any) {
    if (value === undefined || value === null) {
      if (field.required) errors.push(`Missing required field: ${field.name}`);
      return;
    }
    switch (field.kind) {
      case 'string':
        if (typeof value !== 'string') errors.push(`${field.name}: expected string`);
        break;
      case 'number':
        if (typeof value !== 'number') errors.push(`${field.name}: expected number`);
        break;
      case 'boolean':
        if (typeof value !== 'boolean') errors.push(`${field.name}: expected boolean`);
        break;
      case 'enum':
        if (!field.options.includes(value)) errors.push(`${field.name}: must be one of ${field.options.join(', ')}`);
        break;
      case 'object':
        if (typeof value !== 'object') errors.push(`${field.name}: expected object`);
        else for (const sub of field.fields) check(sub, value[sub.name]);
        break;
      case 'array':
        if (!Array.isArray(value)) errors.push(`${field.name}: expected array`);
        else for (const item of value) check(field.item, item);
        break;
    }
  }
  for (const f of config.dataContract.fields) {
    check(f, data?.[f.name]);
  }
  return { ok: errors.length === 0, errors };
}

// ---------------------------------------------------------------------------
// Mapping — external record ↔ ExternalPageBlock
// ---------------------------------------------------------------------------

export interface MapOptions {
  /** Block id. Auto-generated if omitted. */
  id?: string;
  /** Position. Defaults to 'end'. */
  position?: 'start' | 'end';
}

export function mapExternalDataToBlock(
  type: string,
  data: Record<string, any>,
  opts: MapOptions = {},
): ExternalPageBlock | null {
  const config = getExternalBlockConfig(type);
  if (!config) return null;
  const merged = { ...(config.defaultData ?? {}), ...data };
  const id = opts.id ?? `ext-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
  return {
    id,
    type: 'external',
    externalType: type,
    data: merged,
    config: { renderer: config.renderer, category: config.category },
    createdAt: new Date().toISOString(),
  };
}

export function mapBlockToExternalData(block: PageBlock): { type: string; data: Record<string, any> } | null {
  if (block.type !== 'external') return null;
  const ext = block as ExternalPageBlock;
  return { type: ext.externalType, data: ext.data };
}

// ---------------------------------------------------------------------------
// Insertion helper — for external apps that want to add a block to a page
// without poking ctx directly. The host's mutation surface is unchanged;
// this just produces the payload that any addPageBlock call would accept.
// ---------------------------------------------------------------------------

export function buildExternalBlockPayload(
  type: string,
  data: Record<string, any>,
  opts: MapOptions = {},
): ExternalPageBlock | null {
  return mapExternalDataToBlock(type, data, opts);
}

// ---------------------------------------------------------------------------
// Stats
// ---------------------------------------------------------------------------

export function getRegistryStats() {
  const all = listExternalBlockConfigs();
  return {
    total: all.length,
    byCategory: all.reduce<Record<string, number>>((acc, c) => {
      const cat = c.category ?? 'other';
      acc[cat] = (acc[cat] ?? 0) + 1;
      return acc;
    }, {}),
  };
}
